/**
 * Drains the IndexedDB outbox by re-trying each pending message via the
 * regular API. Server uses clientMessageId as an idempotency key, so retries
 * never produce duplicates.
 *
 * Caller is responsible for invalidating React Query caches after the flush
 * (so newly-arrived messages show up in the list).
 */

import { apiClient } from '@/api/client';
import { outbox, type OutboxItem } from './outbox';

interface FlushResult {
  sent: number;
  failed: number;
}

let flushing = false;

export async function flushOutbox(): Promise<FlushResult> {
  if (flushing) return { sent: 0, failed: 0 };
  flushing = true;
  try {
    const items = await outbox.list();
    let sent = 0;
    let failed = 0;
    for (const item of items) {
      const ok = await sendOne(item);
      if (ok) {
        await outbox.delete(item.clientMessageId);
        sent += 1;
      } else {
        await outbox.incrementRetry(item.clientMessageId);
        failed += 1;
      }
    }
    return { sent, failed };
  } finally {
    flushing = false;
  }
}

async function sendOne(item: OutboxItem): Promise<boolean> {
  try {
    if (item.channelId) {
      await apiClient.post(`/api/messages/channel/${item.channelId}`, {
        content: item.content,
        type: item.type ?? 'TEXT',
        replyToId: item.replyToId,
        mediaFileId: item.mediaFileId,
        clientMessageId: item.clientMessageId,
      });
      return true;
    }
    if (item.recipientId) {
      await apiClient.post(`/api/messages/dm/${item.recipientId}`, {
        content: item.content,
        type: item.type ?? 'TEXT',
        replyToId: item.replyToId,
        mediaFileId: item.mediaFileId,
        clientMessageId: item.clientMessageId,
      });
      return true;
    }
    // No target — drop the item, it's malformed
    await outbox.delete(item.clientMessageId);
    return false;
  } catch {
    return false;
  }
}

let listenersWired = false;

/**
 * Wire up auto-flush triggers. Idempotent — only the first call attaches.
 *
 * Triggers:
 *   - browser 'online' event
 *   - one initial flush right after wiring (in case we're online on load
 *     and have leftover items from a previous session)
 */
export function wireOutboxAutoFlush(onFlushed?: (r: FlushResult) => void) {
  if (listenersWired) return;
  listenersWired = true;
  const fire = async () => {
    if (!navigator.onLine) return;
    const r = await flushOutbox();
    if (r.sent > 0) onFlushed?.(r);
  };
  window.addEventListener('online', fire);
  // Initial flush (next tick so React mounts can settle first)
  setTimeout(fire, 500);
}
